import React, { useMemo, useState } from 'react';
import Alert from 'Components/Alert';
import FieldSet from 'Components/FieldSet';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import TextArea from 'Components/Form/TextArea';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from '../components/TdarrTableRowCell';
import { kinds } from 'Helpers/Props';

interface ParsedLine { id: number; time: string; component: string; message: string; isError: boolean; isWarning: boolean; isStep: boolean; isJson: boolean }

function parseLine(line: string, id: number): ParsedLine {
  const timestamp = line.match(/^(\S+)\s+(.*)$/);
  const rest = timestamp?.[2] ?? line;
  const worker = rest.match(/Worker\[([^\]]+)\]:/);
  const node = rest.match(/Node\[([^\]]+)\]:/);
  const firstColon = rest.indexOf(':');
  const message = firstColon >= 0 ? rest.slice(firstColon + 1).replace(/^(?:Node\[[^\]]+\]:)?(?:Worker\[[^\]]+\]:)?/, '') : rest;
  const trimmed = message.trim();
  let isJson = false;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) { try { JSON.parse(trimmed); isJson = true; } catch { /* ordinary log line */ } }
  return { id, time: timestamp?.[1] ?? '—', component: worker ? `Worker ${worker[1]}` : node ? `Node ${node[1]}` : firstColon >= 0 ? rest.slice(0, firstColon) : 'Tdarr', message: trimmed, isError: /\b(error|failed|failure)\b/i.test(trimmed), isWarning: /\b(warn|warning)\b/i.test(trimmed), isStep: /\[Step [^\]]+\]/.test(trimmed), isJson };
}

const columns = [{ name: 'time', label: 'Time', isVisible: true, isSortable: false }, { name: 'component', label: 'Component', isVisible: true, isSortable: false }, { name: 'message', label: 'Message', isVisible: true, isSortable: false }];

export default function ReportParser() {
  const [text, setText] = useState('');
  const lines = useMemo(() => text.split(/\r?\n/).filter(Boolean).map(parseLine), [text]);
  const errors = lines.filter((line) => line.isError).length;
  const warnings = lines.filter((line) => line.isWarning).length;
  const steps = lines.filter((line) => line.isStep).length;
  const json = lines.filter((line) => line.isJson).length;
  return <PageContent title="Report Parser"><PageContentBody>
    <FieldSet legend="Report Input">
      <Alert>Paste Tdarr job-report text to inspect its timeline locally in your browser. Nothing is sent to the server.</Alert>
      <FormGroup size="large"><FormLabel name="reportText">Job Report</FormLabel><TextArea name="reportText" value={text} placeholder="Paste a Tdarr job report here" onChange={({ value }) => setText(value)} /></FormGroup>
    </FieldSet>
    {!!lines.length && <>
      <FieldSet legend="Summary"><Table columns={[{ name: 'name', label: 'Name', isVisible: true, isSortable: false }, { name: 'value', label: 'Value', isVisible: true, isSortable: false }]} horizontalScroll={false}><TableBody>
        <TableRow><TableRowCell>Lines</TableRowCell><TableRowCell>{lines.length}</TableRowCell></TableRow><TableRow><TableRowCell>Steps</TableRowCell><TableRowCell>{steps}</TableRowCell></TableRow>
        <TableRow><TableRowCell>Errors</TableRowCell><TableRowCell>{errors}</TableRowCell></TableRow><TableRow><TableRowCell>Warnings</TableRowCell><TableRowCell>{warnings}</TableRowCell></TableRow><TableRow><TableRowCell>JSON Records</TableRowCell><TableRowCell>{json}</TableRowCell></TableRow>
      </TableBody></Table></FieldSet>
      {(errors > 0 || warnings > 0) && <Alert kind={errors ? kinds.DANGER : kinds.WARNING}>{errors} error-like and {warnings} warning-like log lines were detected.</Alert>}
      <FieldSet legend="Timeline">
        {lines.length > 500 && <Alert kind={kinds.WARNING}>Showing the first 500 of {lines.length} lines.</Alert>}
        <Table columns={columns}><TableBody>{lines.slice(0, 500).map((line) => <TableRow key={line.id}><TableRowCell>{line.time}</TableRowCell><TableRowCell>{line.component}</TableRowCell><TableRowCell className="tdarr-path">{line.message}</TableRowCell></TableRow>)}</TableBody></Table>
      </FieldSet>
      <FieldSet legend="Raw Report"><pre className="tdarr-log">{text}</pre></FieldSet>
    </>}
  </PageContentBody></PageContent>;
}
